import { BreakpointObserver } from '@angular/cdk/layout';
import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

/**
 * Represents the different screen sizes.
 * @internal
 */
type ScreenSize = 'Mobile' | 'TabletPortrait' | 'TabletLandscape' | 'Desktop' | 'Unknown';

/**
 * Custom breakpoints for different screen sizes.
 */
const CustomBreakpoints = {
    Mobile: '(max-width: 599px)',
    TabletPortrait: '(min-width: 600px) and (max-width: 904px)',
    TabletLandscape: '(min-width: 905px) and (max-width: 1151px)',
    Desktop: '(min-width: 1152px)'
};

/**
 * Service to observe changes in window size.
 * Screen size changes trigger currentScreenSize update.
 *
 * @class BreakpointService
 * @typedef {BreakpointService}
 * @implements {OnDestroy}
 */
@Injectable( {
    providedIn: 'root'
} )

export class BreakpointService implements OnDestroy {
    /**  lifecycle hook */
    readonly #destroyed: Subject<void> = new Subject<void>();
    readonly #breakpointObserver = inject( BreakpointObserver );

    /** currentScreenSize signal */
    readonly currentScreenSize = signal<ScreenSize>( 'Unknown' );

    readonly isMobile = computed( () =>
        this.currentScreenSize() === 'Mobile'
    );

    readonly isTablet = computed( () =>
        this.currentScreenSize() === 'TabletPortrait' ||
        this.currentScreenSize() === 'TabletLandscape'
    );

    readonly isTabletPortrait = computed( () =>
        this.currentScreenSize() === 'TabletPortrait'
    );

    /** names of observed breakpoints */
    breakpointNames = new Map<string, ScreenSize>( [
        [ CustomBreakpoints.Mobile, 'Mobile' ],
        [ CustomBreakpoints.TabletPortrait, 'TabletPortrait' ],
        [ CustomBreakpoints.TabletLandscape, 'TabletLandscape' ],
        [ CustomBreakpoints.Desktop, 'Desktop' ]
    ] );


    constructor() {
        this.getCurrentScreenSize();
    }

    /**
     * helper function, to observe breakpoint changes
     * sets currentScreenSize to evaluated breakpoint name
     */
    getCurrentScreenSize() {
        const allBreakpoints = Object.values( CustomBreakpoints );

        /** screen size */
        this.#breakpointObserver
            .observe( allBreakpoints )
            .pipe( takeUntil( this.#destroyed ) )
            .subscribe( result => {
                for ( const query of Object.keys( result.breakpoints ) ) {
                    if ( result.breakpoints[ query ] ) {
                        const breakpoint = this.breakpointNames.get( query ) ?? 'Unknown';
                        this.currentScreenSize.set( breakpoint );
                    }
                }
            } );
    }

    /** lifecycle hook */
    ngOnDestroy() {
        this.#destroyed.next();
        this.#destroyed.complete();
    }
}
